import type { CodeableConcept, Period } from "./common";
import { InvalidTransitionError, PreconditionFailedError } from "./errors";
import type { DomainId } from "./lib/domain-id";
import type { PatientId } from "./patient.model";

/** Stable MedicationStatement identity, independent of the FHIR row locator. */
export type MedicationStatementId = DomainId<"MedicationStatementId">;

export function asMedicationStatementId(value: string): MedicationStatementId {
  if (!value) throw new PreconditionFailedError("A medication statement identity cannot be empty");
  return value as MedicationStatementId;
}

// ============================================================
// MedicationStatement — what this patient is taking (ADR-0017)
//
// A statement of fact, not an order: it records what the patient reports or a
// clinician observed, so it has no fulfilment lifecycle. Only whether the
// medication is still being taken, and whether the record still stands.
// ============================================================

export const MEDICATION_STATUSES = [
  "active",
  "completed",
  "entered-in-error",
  "intended",
  "stopped",
  "on-hold",
  "unknown",
  "not-taken",
] as const;
type MedicationStatus = (typeof MEDICATION_STATUSES)[number];

/** How the drug enters the body. */
export const MEDICATION_ROUTES = [
  "oral",
  "sublingual",
  "topical",
  "transdermal",
  "inhaled",
  "nasal",
  "ophthalmic",
  "otic",
  "rectal",
  "vaginal",
  "subcutaneous",
  "intramuscular",
  "intravenous",
] as const;
export type MedicationRoute = (typeof MEDICATION_ROUTES)[number];

export const DOSE_FREQUENCIES = [
  "daily",
  "twice-daily",
  "three-times-daily",
  "four-times-daily",
  "every-4-hours",
  "every-6-hours",
  "every-8-hours",
  "every-other-day",
  "at-bedtime",
  "weekly",
  "monthly",
] as const;
export type DoseFrequency = (typeof DOSE_FREQUENCIES)[number];

export const DOSE_UNITS = [
  "tablet",
  "capsule",
  "mg",
  "mcg",
  "g",
  "mL",
  "drop",
  "puff",
  "patch",
  "application",
  "unit",
] as const;
export type DoseUnit = (typeof DOSE_UNITS)[number];

/**
 * The common shape of a regimen: how much, how often, which way. Every part is
 * optional — a patient often knows the frequency but not the strength.
 */
export interface MedicationDosage {
  amount?: { value: number; unit: DoseUnit };
  frequency?: DoseFrequency;
  route?: MedicationRoute;
  /** Taken when needed rather than on the schedule alone. */
  asNeeded?: boolean;
}

/** Records that can still be corrected — the rest are history or retracted. */
const TERMINAL: ReadonlySet<MedicationStatus> = new Set(["completed", "stopped", "entered-in-error"]);

/** Only a medication that is being, or is to be, taken can be stopped. */
const STOPPABLE: ReadonlySet<MedicationStatus> = new Set(["active", "intended", "on-hold"]);

export interface MedicationStatementProps {
  id: MedicationStatementId;
  status: MedicationStatus;
  /** The drug. Free text is allowed — patients report brand names. */
  medication: CodeableConcept;
  patientId: PatientId;
  effectiveDateTime?: string;
  effectivePeriod?: Period;
  /** Single-line dosage as written or as reported. */
  dosageText?: string;
  dosage?: MedicationDosage;
  dateAsserted?: string;
}

export class MedicationStatement {
  private state: MedicationStatementProps;

  private constructor(props: MedicationStatementProps) {
    if (!props.patientId) {
      throw new PreconditionFailedError("Medication statement requires a patient");
    }
    if (!props.medication.coding?.length && !props.medication.text) {
      throw new PreconditionFailedError("Medication statement requires a medication");
    }
    MedicationStatement.assertDoseAmount(props.dosage);
    this.state = structuredClone(props);
  }

  static create(input: MedicationStatementProps): MedicationStatement {
    return new MedicationStatement({
      ...structuredClone(input),
      id: asMedicationStatementId(input.id),
    });
  }

  static restore(snapshot: MedicationStatementProps): MedicationStatement {
    return new MedicationStatement(snapshot);
  }

  // ── facts ──

  get id(): MedicationStatementId {
    return this.state.id;
  }

  get patientId(): PatientId {
    return this.state.patientId;
  }

  get status(): MedicationStatus {
    return this.state.status;
  }

  /** Being taken now — what a medication list shows and an interaction check reads. */
  get isBeingTaken(): boolean {
    return this.state.status === "active";
  }

  snapshot(): MedicationStatementProps {
    return structuredClone(this.state);
  }

  // ── operations ──

  /** The patient stopped taking it. */
  stop(): void {
    if (!STOPPABLE.has(this.state.status)) {
      throw new InvalidTransitionError(this.state.status, "MedicationStatement:stop");
    }
    this.state.status = "stopped";
  }

  /** Correct the dosage or the dates. Refused once the record is history. */
  revise(changes: {
    dosageText?: string;
    dosage?: MedicationDosage;
    effectiveDateTime?: string;
    effectivePeriod?: Period;
  }): void {
    if (TERMINAL.has(this.state.status)) {
      throw new PreconditionFailedError(`Cannot update a medication in terminal status '${this.state.status}'`);
    }
    MedicationStatement.assertDoseAmount(changes.dosage);
    Object.assign(this.state, structuredClone(changes));
  }

  private static assertDoseAmount(dosage: MedicationDosage | undefined): void {
    const value = dosage?.amount?.value;
    if (value !== undefined && !(value > 0)) {
      throw new PreconditionFailedError(`A dose amount must be positive (got ${value})`);
    }
  }
}
